import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BookClubSettings } from '@/types/chat';

export function useBookClubSettings(chatId: string | undefined) {
  const [settings, setSettings] = useState<BookClubSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    if (!chatId) {
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('book_club_settings')
        .select('*')
        .eq('chat_id', chatId)
        .maybeSingle();

      if (error) throw error;
      setSettings(data as BookClubSettings | null);
    } catch (err) {
      console.error('Error fetching book club settings:', err);
    } finally {
      setLoading(false);
    }
  }, [chatId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = useCallback(async (updates: Partial<BookClubSettings>) => {
    if (!chatId || !settings) return;
    try {
      const { error } = await supabase
        .from('book_club_settings')
        .update(updates)
        .eq('chat_id', chatId);
      if (error) throw error;
      setSettings(prev => prev ? { ...prev, ...updates } : prev);
    } catch (err) {
      console.error('Error updating book club settings:', err);
      throw err;
    }
  }, [chatId, settings]);

  return { settings, loading, updateSettings, refetch: fetchSettings };
}
